/**
 * Chat Message - Individual message in a marketplace conversation
 */

import { Check, CheckCheck } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface Props {
  content: string;
  messageType: 'text' | 'image' | 'system';
  isOwnMessage: boolean;
  readAt: string | null;
  createdAt: string;
  senderEmail?: string;
}

export default function ChatMessage({
  content,
  messageType,
  isOwnMessage,
  readAt,
  createdAt,
  senderEmail,
}: Props) {
  const { t } = useLanguage();

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `${t('common.time.yesterday', 'Yesterday')} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }

    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // System message
  if (messageType === 'system') {
    return (
      <div className="flex justify-center my-3">
        <div className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-full">
          {content}
        </div>
      </div>
    );
  }

  // Image message
  if (messageType === 'image') {
    return (
      <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3`}>
        <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
          <div
            className={`rounded-2xl overflow-hidden ${
              isOwnMessage
                ? 'bg-primary-600 text-white rounded-br-md'
                : 'bg-gray-100 rounded-bl-md'
            }`}
          >
            <img
              src={content}
              alt={t('chat.sharedImage', 'Shared image')}
              className="max-w-full max-h-64 object-contain"
            />
          </div>
          <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            <span>{formatTime(createdAt)}</span>
            {isOwnMessage && (
              readAt ? (
                <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  // Text message
  return (
    <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
        {!isOwnMessage && senderEmail && (
          <p className="text-xs text-gray-500 mb-1 ml-3">
            {senderEmail.split('@')[0]}
          </p>
        )}
        <div
          className={`px-4 py-2.5 rounded-2xl ${
            isOwnMessage
              ? 'bg-primary-600 text-white rounded-br-md'
              : 'bg-gray-100 text-gray-900 rounded-bl-md'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">{content}</p>
        </div>
        <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${isOwnMessage ? 'justify-end mr-1' : 'justify-start ml-3'}`}>
          <span>{formatTime(createdAt)}</span>
          {isOwnMessage && (
            readAt ? (
              <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )
          )}
        </div>
      </div>
    </div>
  );
}
